import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient } from "../sdk";
import {
  RawBucket,
  MultiFeedBucketsData,
  MultiFeedBucketData,
} from "../sdk/types/api";
import {
  Bucket,
  BucketsConfig,
  transformRawBucketToBucket,
  convertMultiFeedToRawBucket,
  isRawBucket,
} from "../sdk/types/data";
import { useSubscription } from "../context/SubscriptionContext";

/**
 * Hook for managing buckets data for a wallet address.
 * Uses WebSocket with optional initial REST fetch.
 */
export function useBuckets(config: BucketsConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const refreshIntervalRef = useRef<number>(0);

  /**
   * Transform bucket data to Bucket format
   * Handles both RawBucket and MultiFeedBucketData types
   */
  const transformBucket = useCallback(
    (bucket: RawBucket | MultiFeedBucketData): Bucket => {
      // Convert MultiFeedBucketData to RawBucket if needed
      const rawBucket: RawBucket = isRawBucket(bucket)
        ? bucket
        : convertMultiFeedToRawBucket(bucket);

      // Transform raw API data to Bucket type (handles satoshi conversion)
      return transformRawBucketToBucket(rawBucket);
    },
    [],
  );

  /**
   * Fetch buckets from API
   */
  const fetchBuckets = useCallback(async () => {
    if (!infoClientRef.current) return;

    setError("");

    try {
      if (!config.walletAddress) {
        setError("Wallet address is required");
        setBuckets([]);
        return;
      }

      // Fetch buckets from API
      const response = await infoClientRef.current.getBuckets(
        config.walletAddress,
      );

      const bucketsData = Array.isArray(response) ? response : [];

      if (!Array.isArray(response)) {
        console.warn("[useBuckets] getBuckets returned non-array:", response);
      }

      // Transform API response to Bucket format
      // Uses shared transformation function that handles satoshi conversion
      const transformedBuckets: Bucket[] = bucketsData.map(transformBucket);

      // Compare with current buckets to avoid unnecessary state updates
      setBuckets((prev) => {
        if (prev.length !== transformedBuckets.length) {
          return transformedBuckets;
        }
        // Deep comparison - only update if buckets actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(transformedBuckets);
        return prevStr === newStr ? prev : transformedBuckets;
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load buckets";
      setError(errorMessage);
      console.error("Failed to fetch buckets:", err);
    } finally {
      setLoading(false);
    }
  }, [config.walletAddress, transformBucket]);

  /**
   * Handle bucket updates from WebSocket (multi-feed buckets sub-channel)
   */
  const handleBucketsUpdate = useCallback(
    (data: MultiFeedBucketsData) => {
      if (data.buckets && Array.isArray(data.buckets)) {
        const transformedBuckets: Bucket[] = data.buckets.map(transformBucket);

        // Compare with current buckets to avoid unnecessary state updates
        setBuckets((prev) => {
          if (prev.length !== transformedBuckets.length) {
            return transformedBuckets;
          }
          // Deep comparison - only update if buckets actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(transformedBuckets);
          return prevStr === newStr ? prev : transformedBuckets;
        });
        setLoading(false);
      }
    },
    [transformBucket],
  );

  const { subscribe, unsubscribe, isConnected: contextConnected } = useSubscription();

  // Initialize clients and set up WebSocket (key-based) or polling
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    fetchBuckets();
    setIsConnected(contextConnected);

    const bucketsCallback = (data: unknown) =>
      handleBucketsUpdate(data as MultiFeedBucketsData);

    if (useWebSocket) {
      subscribe("buckets", bucketsCallback);
      if (!contextConnected && config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(
          () => fetchBuckets(),
          config.refreshInterval,
        );
      }
      return () => {
        if (refreshIntervalRef.current !== 0) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
        unsubscribe("buckets", bucketsCallback);
      };
    }

    if (config.refreshInterval && config.refreshInterval > 0) {
      refreshIntervalRef.current = window.setInterval(
        () => fetchBuckets(),
        config.refreshInterval,
      );
    }
    return () => {
      if (refreshIntervalRef.current !== 0) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = 0;
      }
    };
  }, [
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.refreshInterval,
    useWebSocket,
    contextConnected,
    subscribe,
    unsubscribe,
    fetchBuckets,
    handleBucketsUpdate,
  ]);

  return {
    buckets,
    loading,
    error,
    isConnected,
    refresh: fetchBuckets,
  };
}
